import { BaseSandbox } from '@app/core/store/base.sandbox';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CoreState } from '@app/core/store/core.slice';
import { filter, Observable, Subject } from 'rxjs';
import { WorkService } from '@app/core/services/work.service';
import { MyWorkHistoryItem } from '@app/core/types/work/work.types';
import {
  getNextPaymentDate,
  getWorkHistory,
  nextPaymentDateState,
  workHistoryState,
} from '@app/work/store/work.slice';
import { map, tap } from 'rxjs/operators';
import { DocumentService } from '@app/documents/services/document.service';
import { FileService } from '@app/core/services/file.service';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { getFullFileName } from '@app/shared/utils/content-type.functions';
import { firstValueFrom } from 'rxjs';
import { showNextPaymentDateSelector, showWorkedHoursSelector } from '@app/home/store/home.slice';
import { FeatureFlagStore } from '@app/core/feature-flags/feature-flag.store';
import { FeatureFlags } from '@app/core/feature-flags/feature-flag.types';

@Injectable({ providedIn: 'any' })
export class WorkSandbox extends BaseSandbox {
  private readonly documentService = inject(DocumentService);
  private readonly fileService = inject(FileService);
  private readonly toastSandbox = inject(ToastSandbox);
  private readonly featureFlagStore = inject(FeatureFlagStore);

  timeRegistrationFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.TimeRegistration);
  expensesFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Expenses);
  requestsFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Requests);
  balanceFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Balance);
  payslipFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Payslips);
  invoiceFeatureFlag = this.featureFlagStore.getFeatureFlag(FeatureFlags.Invoices);

  nextPaymentDate$: Observable<Date> = this.store.select(nextPaymentDateState.data).pipe(
    filter((date) => !!date),
    map((paymentDate) => new Date(paymentDate))
  );

  workHistory$: Observable<MyWorkHistoryItem[]> = this.store.select(workHistoryState.data);
  workHistoryLoading$: Observable<boolean> = this.store.select(workHistoryState.loading);

  scrollTrigger$ = new Subject<void>();
  isLoadingLatestPayslip = false;
  isLoadingLatestInvoice = false;

  showWorkedHours$ = this.store.select(showWorkedHoursSelector);

  showNextPaymentDate$ = this.store.select(showNextPaymentDateSelector).pipe(
    tap((v) => {
      if (v) {
        this.getNextPaymentDate();
      }
    })
  );

  workHistoryForWeekOfYear$: (weekNumber: number, year: number) => Observable<MyWorkHistoryItem> = (
    weekNumber: number,
    year: number
  ) =>
    this.workHistory$.pipe(
      filter((workHistory) => !!workHistory?.length),
      map((workHistory) => workHistory.find((item) => item.year === year && item.weekNumber === weekNumber))
    );

  constructor(
    store: Store<CoreState>,
    private workService: WorkService
  ) {
    super(store);
  }

  getNextPaymentDate(): void {
    this.load(getNextPaymentDate, {}, this.workService.getNextPaymentDate());
  }

  getWorkHistory(): void {
    this.load(getWorkHistory, {}, this.workService.getMyWorkHistory());
  }

  async openLatestPayslip(): Promise<void> {
    try {
      this.isLoadingLatestPayslip = true;
      const blob = await firstValueFrom(this.documentService.downloadLatestPayslip());
      if (blob) {
        await this.fileService.previewFile(blob, getFullFileName('payslip', blob.type));
      }
    } catch {
      this.toastSandbox.showToast({ key: 'error.preview-unavailable' });
    } finally {
      this.isLoadingLatestPayslip = false;
    }
  }

  async openLatestInvoice(): Promise<void> {
    try {
      this.isLoadingLatestInvoice = true;
      const blob = await firstValueFrom(this.documentService.downloadLatestInvoice());
      if (blob) {
        await this.fileService.previewFile(blob, getFullFileName('invoice', blob.type));
      }
    } catch {
      this.toastSandbox.showToast({ key: 'error.preview-unavailable' });
    } finally {
      this.isLoadingLatestInvoice = false;
    }
  }
}
